import React, { useEffect, useState } from "react";
import {
    Box,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Avatar,
    Button,
    Divider,
    Grid,
    TextField,
    Switch,
    Toolbar,
    Typography,
    Chip,
    CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Save, Refresh, Search } from "@mui/icons-material";
import {
    getDirectoryPermissions,
    setDirectoryPermissions,
} from "api/canshare/canshareApi";
import { DialogHelp } from "components";
import { useNotificationProvider } from "hooks";

const Permissions = ({
    dirId,
    custId,
    openDialog,
    onCloseDialog,
    historyRef,
}) => {
    const [permissions, setPermissions] = useState([]);
    const [initialPermissions, setInitialPermissions] = useState([]);
    const [pending, setPending] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [expanded, setExpanded] = useState(false); // single-expand state
    const { notifySuccess, notifyError } = useNotificationProvider();

    const fetchPermissions = async () => {
        try {
            setPending(true);
            const response = await getDirectoryPermissions(dirId, custId);
            const dataCopy = JSON.parse(JSON.stringify(response)); // deep copy
            setPermissions(dataCopy);
            setInitialPermissions(dataCopy);
            setPending(false);
        } catch (error) {
            console.error("Error fetching permissions", error);
            setPending(false);
        }
    };

    useEffect(() => {
        if (openDialog) {
            fetchPermissions();
            setExpanded(false);
        }
    }, [dirId, openDialog]);

    const handleToggleChange = (userId, type, checked) => {
        const value = checked ? "1" : "0";
        setPermissions((prevPermissions) =>
            prevPermissions.map((user) =>
                user.UserId === userId
                    ? {
                          ...user,
                          [type === "read" ? "cbRead" : "cbWrite"]: value,
                      }
                    : user
            )
        );
    };

    const handleAccordionChange = (userId) => (event, isExpanded) => {
        setExpanded(isExpanded ? userId : false);
    };

    // Function to toggle all permissions off (set to "0")
    const handleToggleAllOff = () => {
        setPermissions((prevPermissions) =>
            prevPermissions.map((user) => ({
                ...user,
                cbRead: "0",
                cbWrite: "0",
            }))
        );
    };

    // New function to toggle all permissions on (set to "1")
    const handleToggleAllOn = () => {
        setPermissions((prevPermissions) =>
            prevPermissions.map((user) => ({
                ...user,
                cbRead: "1",
                cbWrite: "1",
            }))
        );
    };

    const handleSave = async () => {
        try {
            setPending(true);
            const payload = { dirId, custId, permissions };
            const res = await setDirectoryPermissions(payload);
            if (res.Success) {
                notifySuccess(
                    res.Message || "Permissions updated successfully"
                );
                await fetchPermissions();
            } else {
                notifyError(res.Message || "Error updating permissions");
            }
            setPending(false);
            if (onCloseDialog) onCloseDialog();
        } catch (error) {
            console.error("Error saving permissions", error);
            notifyError("Failed to update permissions");
            setPending(false);
        }
    };

    const handleReset = () => {
        setPermissions(JSON.parse(JSON.stringify(initialPermissions)));
    };

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
    };

    const filteredPermissions = permissions.filter((user) =>
        user.userName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const renderContent = () => {
        if (pending) {
            return (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="300px"
                >
                    <CircularProgress />
                </Box>
            );
        }
        return (
            <Box>
                {/* Responsive Toolbar */}
                <Toolbar sx={{ mb: 2, px: { xs: 1, sm: 2 } }}>
                    <TextField
                        variant="outlined"
                        label="Search Users"
                        size="small"
                        value={searchTerm}
                        onChange={handleSearch}
                        InputProps={{
                            startAdornment: <Search sx={{ mr: 1 }} />,
                        }}
                        sx={{ flexGrow: 1, minWidth: "150px" }}
                    />
                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<Refresh />}
                        onClick={handleReset}
                        sx={{ ml: { xs: 1, sm: 2 } }}
                    >
                        Reset
                    </Button>
                    {/* Button to toggle all permissions off */}
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={handleToggleAllOff}
                        sx={{ ml: { xs: 1, sm: 2 } }}
                    >
                        Toggle All Off
                    </Button>
                    {/* New Button to toggle all permissions on */}
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleToggleAllOn}
                        sx={{ ml: { xs: 1, sm: 2 } }}
                    >
                        Toggle All On
                    </Button>
                </Toolbar>
                <Divider sx={{ mb: 2 }} />
                {/* Accordion List */}
                <Box>
                    {filteredPermissions.length ? (
                        filteredPermissions.map((user) => (
                            <Accordion
                                key={user.UserId}
                                expanded={expanded === user.UserId}
                                onChange={handleAccordionChange(user.UserId)}
                                sx={{
                                    mb: 2,
                                    boxShadow: 3,
                                    borderRadius: 2,
                                    "&:before": { display: "none" },
                                    border: "1px solid",
                                    borderColor: "divider",
                                    transition: "all 0.3s ease",
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls={`panel-${user.UserId}-content`}
                                    id={`panel-${user.UserId}-header`}
                                    sx={{
                                        px: { xs: 1, sm: 2 },
                                        py: { xs: 0.5, sm: 1 },
                                    }}
                                >
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        width="100%"
                                    >
                                        <Avatar
                                            sx={{
                                                bgcolor: "primary.main",
                                                mr: { xs: 1, sm: 2 },
                                            }}
                                        >
                                            {user.userName
                                                .charAt(0)
                                                .toUpperCase()}
                                        </Avatar>
                                        <Box flexGrow={1}>
                                            <Typography
                                                variant="subtitle1"
                                                sx={{
                                                    fontWeight: 500,
                                                    fontSize: {
                                                        xs: "0.9rem",
                                                        sm: "1rem",
                                                    },
                                                }}
                                            >
                                                {user.userName}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    fontSize: {
                                                        xs: "0.75rem",
                                                        sm: "0.875rem",
                                                    },
                                                }}
                                            >
                                                <Chip
                                                    label={
                                                        user.cbRead === "1"
                                                            ? "Read: Yes"
                                                            : "Read: No"
                                                    }
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{
                                                        backgroundColor:
                                                            user.cbRead === "1"
                                                                ? "#ebfae8"
                                                                : "#faebe8",
                                                        color: "black",
                                                        fontSize: "inherit",
                                                    }}
                                                />{" "}
                                                <Chip
                                                    label={
                                                        user.cbWrite === "1"
                                                            ? "Edit: Yes"
                                                            : "Edit: No"
                                                    }
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{
                                                        backgroundColor:
                                                            user.cbWrite === "1"
                                                                ? "#ebfae8"
                                                                : "#faebe8",
                                                        color: "black",
                                                        fontSize: "inherit",
                                                    }}
                                                />
                                            </Typography>
                                        </Box>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails
                                    sx={{
                                        px: { xs: 1, sm: 3 },
                                        py: { xs: 1, sm: 2 },
                                    }}
                                >
                                    <Grid
                                        container
                                        spacing={2}
                                        alignItems="center"
                                    >
                                        <Grid item xs={6}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 500,
                                                    fontSize: {
                                                        xs: "0.8rem",
                                                        sm: "0.875rem",
                                                    },
                                                }}
                                            >
                                                Read Permission
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Switch
                                                checked={user.cbRead === "1"}
                                                onChange={(e) =>
                                                    handleToggleChange(
                                                        user.UserId,
                                                        "read",
                                                        e.target.checked
                                                    )
                                                }
                                                color="primary"
                                                size="small"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 500,
                                                    fontSize: {
                                                        xs: "0.8rem",
                                                        sm: "0.875rem",
                                                    },
                                                }}
                                            >
                                                Edit Permission
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Switch
                                                checked={user.cbWrite === "1"}
                                                onChange={(e) =>
                                                    handleToggleChange(
                                                        user.UserId,
                                                        "write",
                                                        e.target.checked
                                                    )
                                                }
                                                color="secondary"
                                                size="small"
                                            />
                                        </Grid>
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>
                        ))
                    ) : (
                        <Typography variant="body2" align="center">
                            No users found.
                        </Typography>
                    )}
                </Box>
            </Box>
        );
    };

    return (
        <DialogHelp
            open={openDialog}
            onClose={onCloseDialog}
            title={
                <Box display="flex" alignItems="center">
                    <Typography variant="h6">User Permissions</Typography>
                    <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ mx: { xs: 1, sm: 2 } }}
                    />
                    <Typography variant="h6">
                        {historyRef.current &&
                        historyRef.current[historyRef.current.length - 1]?.name
                            ? historyRef.current[historyRef.current.length - 1]
                                  .name
                            : ""}
                    </Typography>
                </Box>
            }
            onSave={handleSave}
            saveButtonLabel="Save Changes"
            saveButtonIcon={<Save />}
            content={renderContent()}
        />
    );
};

export default Permissions;
